import honeybadger from '@honeybadger-io/js';
import { clearStoredUserData } from '@/hooks/use-onboarding-tour';
import { toast } from '@/components/toast/toast';

interface PostData {
	[key: string]:
		| string
		| number
		| boolean
		| PostData
		| File
		| Array<PostData>
		| undefined;
}
const NETWORK_ERRORS = {
	NO_AUTH: 'no_auth',
} as const;

type HttpHeaders = Record<string, string | number | boolean | undefined>;

type HttpRequestConfig = {
	headers?: HttpHeaders;
	responseType?: 'json' | 'blob';
	// Opt out of the 401/403 auto-logout for endpoints where a 403 means "not
	// your resource" rather than "your session is gone", and the caller needs
	// to report it instead of being thrown out of the app.
	keepSessionOnAuthError?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HttpResponse<Data = any> = {
	data: Data;
	status: number;
};

class HttpError extends Error {
	status?: number;
	response?: HttpResponse<unknown>;

	constructor(message: string, response?: HttpResponse<unknown>) {
		super(message);
		this.name = 'HttpError';
		this.status = response?.status;
		this.response = response;
	}
}

function isHttpError(error: unknown): error is HttpError {
	return error instanceof HttpError;
}

function logout() {
	clearStoredUserData();
	honeybadger.clear();
	window.location.replace('/');
}

const AUTO_LOGOUT_CODES = [401, 403];

function logoutOnAuthError(status: number, url: string) {
	if (AUTO_LOGOUT_CODES.includes(status) && !url.includes('login')) {
		logout();
	}
}

function isAbsoluteUrl(url: string) {
	return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
}

function buildUrl(baseUrl: string, url: string) {
	if (!baseUrl || isAbsoluteUrl(url)) {
		return url;
	}
	if (!url) {
		return baseUrl;
	}
	return `${baseUrl.replace(/\/?\/$/, '')}/${url.replace(/^\/+/, '')}`;
}

function isRawBody(data: unknown): data is BodyInit {
	return (
		typeof data === 'string' ||
		data instanceof FormData ||
		data instanceof Blob ||
		data instanceof URLSearchParams ||
		data instanceof ArrayBuffer
	);
}

async function parseResponseBody(response: Response, responseType: string) {
	if (responseType === 'blob') {
		return response.blob();
	}
	const text = await response.text();
	if (!text) {
		return text;
	}
	try {
		return JSON.parse(text);
	} catch {
		return text;
	}
}

function createHttpClient(baseUrl: string = '') {
	async function request<Data>(
		method: string,
		url: string,
		data?: unknown,
		config: HttpRequestConfig = {},
	): Promise<HttpResponse<Data>> {
		const headers: Record<string, string> = {
			Accept: 'application/json, text/plain, */*',
		};
		for (const [name, value] of Object.entries(config.headers ?? {})) {
			if (value !== undefined) {
				headers[name] = String(value);
			}
		}

		let body: BodyInit | undefined;
		if (data !== undefined && data !== null) {
			if (isRawBody(data)) {
				body = data;
			} else {
				body = JSON.stringify(data);
				headers['Content-Type'] ??= 'application/json';
			}
		}

		let response: Response;
		try {
			response = await fetch(buildUrl(baseUrl, url), {
				method,
				headers,
				body,
			});
		} catch {
			throw new HttpError('Network Error');
		}

		const result: HttpResponse<Data> = {
			data: await parseResponseBody(
				response,
				config.responseType ?? 'json',
			),
			status: response.status,
		};

		if (!response.ok) {
			if (!config.keepSessionOnAuthError) {
				logoutOnAuthError(response.status, url);
			}
			throw new HttpError(
				`Request failed with status code ${response.status}`,
				result,
			);
		}

		return result;
	}

	return {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		get: <Data = any>(url: string, config?: HttpRequestConfig) =>
			request<Data>('GET', url, undefined, config),
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		delete: <Data = any>(url: string, config?: HttpRequestConfig) =>
			request<Data>('DELETE', url, undefined, config),
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		post: <Data = any>(
			url: string,
			data?: unknown,
			config?: HttpRequestConfig,
		) => request<Data>('POST', url, data, config),
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		put: <Data = any>(
			url: string,
			data?: unknown,
			config?: HttpRequestConfig,
		) => request<Data>('PUT', url, data, config),
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		patch: <Data = any>(
			url: string,
			data?: unknown,
			config?: HttpRequestConfig,
		) => request<Data>('PATCH', url, data, config),
	};
}

type HttpClient = ReturnType<typeof createHttpClient>;

function getAuthorization() {
	const getToken = JSON.parse(localStorage.getItem('user-info')!);

	if (getToken === null) {
		throw new Error('No credentials available', {
			cause: NETWORK_ERRORS.NO_AUTH,
		});
	}

	const authToken: string = getToken['access_token'];
	return `Bearer ${authToken}`;
}

class Http {
	client: HttpClient;
	constructor(baseUrl: string) {
		this.client = createHttpClient(baseUrl);
	}

	get = async <
		ResponseType extends
			| Record<string, unknown>
			| Record<string, unknown>[],
	>(
		url: string,
		headers: PostData = {},
	) => {
		try {
			const authorizationToken = getAuthorization();
			const authHeader = authorizationToken
				? {
						Authorization: authorizationToken,
					}
				: {};
			const config: HttpRequestConfig = {
				headers: {
					...(headers as HttpHeaders),
					...authHeader,
				},
			};
			const response = await this.client.get<ResponseType>(url, config);
			return response?.data;
		} catch (error) {
			if (
				error &&
				typeof error === 'object' &&
				'cause' in error &&
				error.cause === NETWORK_ERRORS.NO_AUTH
			) {
				toast({
					mode: 'destructive',
					description: 'No tienes autorización',
				});
			} else {
				console.error(error);
			}
			throw error;
		}
	};

	getBlob = async (
		url: string,
		headers: PostData = {},
		isAnonimous: boolean = true,
	) => {
		try {
			let authorizationToken = '';
			if (!isAnonimous) {
				authorizationToken = getAuthorization();
			}
			const authHeader = authorizationToken
				? {
						Authorization: authorizationToken,
					}
				: {};
			const config: HttpRequestConfig = {
				headers: {
					...(headers as HttpHeaders),
					...authHeader,
				},
				responseType: 'blob',
			};
			const response = await this.client.get<Blob>(url, config);
			return response?.data;
		} catch (error) {
			if (isHttpError(error)) {
				console.error(error);
			} else if (
				error &&
				typeof error === 'object' &&
				'cause' in error &&
				error.cause === NETWORK_ERRORS.NO_AUTH
			) {
				toast({
					mode: 'destructive',
					description: 'No tienes autorización',
				});
			}
		}
	};

	post = async <ResponseType extends Record<string, unknown>>(
		url: string,
		data: PostData | null,
		isAnonimous: boolean = false,
	) => {
		let authorizationToken = '';
		if (!isAnonimous) {
			authorizationToken = getAuthorization();
		}

		const authHeader = authorizationToken
			? {
					Authorization: authorizationToken,
				}
			: {};
		const config = {
			headers: {
				...authHeader,
			},
		};
		try {
			const response = await this.client.post<ResponseType>(
				url,
				data,
				config,
			);
			return { ...response.data, status: response.status };
		} catch (error) {
			if (isHttpError(error)) {
				throw error;
			} else {
				throw new Error(`${error}`);
			}
		}
	};

	put = async <ResponseType extends Record<string, unknown>>(
		url: string,
		data: PostData | null,
	) => {
		const authorizationToken = getAuthorization();
		const authHeader = authorizationToken
			? {
					Authorization: authorizationToken,
				}
			: {};

		const config = {
			headers: {
				...authHeader,
			},
		};
		try {
			const response = await this.client.put<ResponseType>(
				url,
				data,
				config,
			);
			return response.data;
		} catch (error) {
			if (isHttpError(error)) {
				throw error;
			} else {
				throw new Error(`${error}`);
			}
		}
	};

	patch = async <ResponseType extends Record<string, unknown>>(
		url: string,
		data: PostData | null,
		keepSessionOnAuthError: boolean = false,
	) => {
		const authorizationToken = getAuthorization();
		const authHeader = authorizationToken
			? {
					Authorization: authorizationToken,
				}
			: {};

		const config: HttpRequestConfig = {
			headers: {
				...authHeader,
			},
			keepSessionOnAuthError,
		};
		try {
			const response = await this.client.patch<ResponseType>(
				url,
				data,
				config,
			);
			return response.data;
		} catch (error) {
			if (isHttpError(error)) {
				throw error;
			} else {
				throw new Error(`${error}`);
			}
		}
	};

	delete = async <ResponseType extends Record<string, unknown>>(
		url: string,
	) => {
		const authorizationToken = getAuthorization();
		const authHeader = authorizationToken
			? {
					Authorization: authorizationToken,
				}
			: {};

		const config = {
			headers: {
				...authHeader,
			},
		};
		try {
			const response = await this.client.delete<ResponseType>(
				url,
				config,
			);
			return response.data;
		} catch (error) {
			if (isHttpError(error)) {
				throw error;
			} else {
				throw new Error(`${error}`);
			}
		}
	};
}

const http = new Http(import.meta.env.VITE_API_URL ?? '');

const rawHttp = createHttpClient();

export { rawHttp, http, HttpError, isHttpError };
export type { HttpResponse };
